/*
 * Models for AgentAssist models, including the EPC Queue rules, EPC Migration Codes and EPC Category Code Lists
 */

define([
  "backbone",
  "underscore",
  "jquery",
  "configuration/properties",
  "messaging/messenger",
  "i18n/i18n",
], function (Backbone, _, $, Properties, Messenger) {
  const TopicAgentAssistModel = {};

  //Model for ePC codes obtained from the PCAS server
  TopicAgentAssistModel.ePCCategoryCode = Backbone.Model.extend({
    defaults: {
      parentCode: "Z07", //default top level code
      code: "",
      hasSubgroups: true,
      folder: true,
    },
    subCategories: function () {
      if (this.get("folder") && !this.get("hasSubgroups")) {
        //only return cat codes if the model is a folder without sub folders
        if (!this.subCol) {
          this.subCol = new TopicAgentAssistModel.ePCCategoryCodes();
          this.subCol.url = this.subGroupUrl();
          this.subCol.fetch();
          this.subCol.once(
            "sync",
            function () {
              //pass parentCode to children
              this.subCol.each(function (code) {
                code.set("parentCode", this.get("parentCode"));
              }, this);
            },
            this,
          );
        }
        return this.subCol;
      } else {
        //return empty collection
        return new TopicAgentAssistModel.ePCCategoryCodes();
      }
    },
    subGroups: function () {
      if (this.get("folder")) {
        //only return subgroups if this model is a folder
        if (!this.subCol) {
          this.subCol = new TopicAgentAssistModel.ePCCategoryCodes();
          this.subCol.url = this.subGroupUrl();
          this.subCol.fetch();
          this.subCol.once(
            "sync",
            function () {
              this.subCol.each(function (code) {
                code.set("parentCode", this.get("parentCode"));
              }, this);
            },
            this,
          );
        }
        return this.subCol;
      } else {
        //return empty collection
        return new TopicAgentAssistModel.ePCCategoryCodes();
      }
    },
    subGroupUrl: function () {
      const kbId = Messenger.reqres.request("active:kb");
      if (this.get("parentCode") !== this.get("code")) {
        return (
          `${Properties.rootURL}/${kbId}/` +
          `pcas/category/codes/searchParent` +
          `?code=${encodeURIComponent(this.get("parentCode"))}&parentCode=${encodeURIComponent(
            this.get("code"),
          )}`
        );
      } else {
        return (
          `${Properties.rootURL}/${kbId}/` +
          `pcas/category/codes/search` +
          `?code=${encodeURIComponent(this.get("parentCode"))}`
        );
      }
    },
  });

  //Collection for lists of ePC codes from the PCAS server, including sub folders containing additional codes
  TopicAgentAssistModel.ePCCategoryCodes = Backbone.Collection.extend({
    model: TopicAgentAssistModel.ePCCategoryCode,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
  });

  // Model for ePC code migration (weird name...I guess this is like a binding) which assignees
  // a cat code to a response
  TopicAgentAssistModel.ePCCategoryCodeMigration = Backbone.Model.extend({
    // do not let "id" be the id attribute since it can be the same value for different migrations
    idAttribute: "_id",
    defaults: {
      categoryId: "",
      code: "",
    },
  });

  //Collection of ePC code migrations for a response
  TopicAgentAssistModel.ePCCategoryCodeMigrations = Backbone.AtomicCollection.extend({
    model: TopicAgentAssistModel.ePCCategoryCodeMigration,
    initializeCustom: function () {
      this.on("change:categoryId", (updatedCode) => {
        updatedCode.set("code", "");
      });
    },
    addMigration: function () {
      const newMigration = new TopicAgentAssistModel.ePCCategoryCodeMigration();
      this.add(newMigration);
      return newMigration;
    },
    toJSON: function (options) {
      if (options && options.saving) {
        // filter out any dummy migrations
        // (just the id -- used to delete any migrations from a response)
        return this.filter((migration) => !_.isEmpty(migration.get("categoryId")));
      }
      return this.constructor.__super__.toJSON.apply(this, arguments);
    },
    _buildPathToResponseMap: function (map, path, conversationNode) {
      const currentPath = path + conversationNode.get("description");
      map[currentPath] = conversationNode.get("responseId");
      conversationNode.decisionCol.each(function (node) {
        this._buildPathToResponseMap(map, currentPath + "  ", node);
      }, this);
    },
  });

  //Get the top level ePC code directories.  These are 'A-Address', 'B-Case', 'C-Issue' and are the starting point
  //for selecting ePC codes
  let topLevel;
  const setUpEpcCodes = function () {
    const topLevelDir = new TopicAgentAssistModel.ePCCategoryCodes();
    const aCode = new TopicAgentAssistModel.ePCCategoryCode({
      name: $.t("topic.epc.epcCodeA"),
      code: "A",
      hasSubgroups: false,
      folder: true,
    });
    const bCode = new TopicAgentAssistModel.ePCCategoryCode({
      name: $.t("topic.epc.epcCodeB"),
      code: "B",
      hasSubgroups: false,
      folder: true,
    });
    const cCode = new TopicAgentAssistModel.ePCCategoryCode({
      name: $.t("topic.epc.epcCodeC"),
      code: "C",
      hasSubgroups: false,
      folder: true,
    });
    const fCode = new TopicAgentAssistModel.ePCCategoryCode({
      name: $.t("topic.epc.epcCodeF"),
      code: "F",
      hasSubgroups: false,
      folder: true,
    });
    topLevelDir.add([aCode, bCode, cCode, fCode]);
    topLevel = new TopicAgentAssistModel.ePCCategoryCode({ name: $.t("topic.epc.topLevelName") });
    topLevel.subCol = topLevelDir; //assign the A,B,C folders as the subgroups of the top level directory
  };
  //call the above function once to set up the top level when we first load the site
  setUpEpcCodes();

  //When we change kbs, reset the epc code tree, so that we can get that KB's epc codes
  Messenger.vent.on(
    "kb:changed",
    function () {
      setUpEpcCodes();
    },
    this,
  );

  Messenger.reqres.setHandler(
    "epc:codes",
    function () {
      return topLevel;
    },
    this,
  );

  //Get the values for the passed category code (ePCCategoryCodeMigration model)
  Messenger.reqres.setHandler(
    "epc:code:values",
    function (epcCode) {
      const topLevel = new TopicAgentAssistModel.ePCCategoryCode({
        name: epcCode.get("categoryId"),
        parentCode: epcCode.get("categoryId"),
        code: epcCode.get("categoryId"),
      });
      topLevel.trigger("request"); //fake a request
      const subObjects = topLevel.subGroups();
      subObjects.once(
        "sync",
        function () {
          if (subObjects.length > 0) {
            topLevel.set("hasSubgroups", subObjects.at(0).get("folder"));
          }
          topLevel.trigger("fetched");
          topLevel.trigger("sync");
        },
        this,
      );
      return topLevel;
    },
    this,
  );

  return TopicAgentAssistModel;
});
